import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { OrgMembers } from '../components/OrgMembers.js';
import { EnterpriseSsoPanel } from '../components/EnterpriseSsoPanel.js';
import { api, ApiError } from '../api/client.js';
import type { AuditEntry, MyOrg, OrgRole, TrialPlan } from '../api/client.js';
import { useAuth } from '../state/auth.js';
import { navigateTo } from '../lib/navigation.js';
import { useT } from '../state/i18n.js';

// 워크스페이스 관리 — 생성·멤버·초대·감사로그의 유일한 웹 표면.
// 이 화면이 없으면 org 생성은 운영자 curl 대행이고 2번째 사용자는 영원히 못 들어온다(감사 BLOCKER).
//
// 서버 계약(실측, apps/api/src/modules/orgs/*):
//  - POST /orgs, GET /orgs 는 루트 도메인에서도 유효(테넌트 무관). 로그인 필수.
//  - POST /orgs 는 이메일 미검증 계정에 403 — 서브도메인은 영구 선점 자산이라서다.
//    폼은 항상 띄우고(낡은 클라 판정 방지) 서버 문구를 그대로 노출하되, 미검증이면 미리 경고한다.
//  - 서브도메인 규칙(3~40자 소문자·숫자·하이픈, 양끝 하이픈 금지)·예약어·중복·보유상한은
//    전부 서버가 판정한다. 여기서는 규칙 힌트만 주고 거부 문구를 그대로 보여준다.
//  - ★생성 = DB 의 Org 행 + 라우팅 준비일 뿐, 서브도메인 DNS 는 운영자 작업이 따로 필요하다.
//    "주소가 즉시 살아난다"고 말하면 거짓 — 생성 직후 접속 불가일 수 있음을 정직하게 고지한다.
//  - 멤버/초대/감사(GET /orgs/:id/…)는 org ADMIN 이상 — 비ADMIN 은 호출하지 않는다.
//  - OWNER는 워크스페이스당 1회, 결제수단 없이 30일 체험하거나 PayPal 월 구독을 시작할 수 있다.
//
// ★언어: 이 화면의 모든 유저 노출 문자열은 t({ko,en}) 로만 낸다(하드코딩 금지) —
//   기본 언어는 en(state/i18n.tsx initialLang). 라벨 맵도 {ko,en} 쌍으로 두고 렌더 시 해석한다.

type Phase = 'loading' | 'signin' | 'error' | 'ready';
type AuditState = 'hidden' | 'loading' | 'ready' | 'error';

type Dict = { ko: string; en: string };

const ROLE_LABEL: Record<OrgRole, Dict> = {
  OWNER: { ko: '소유자', en: 'Owner' },
  ADMIN: { ko: '관리자', en: 'Admin' },
  MEMBER: { ko: '멤버', en: 'Member' },
};
// api/common/roles.ts ORG_RANK 미러 — 감사로그·멤버 관리 노출 판정(최종 판정은 서버).
const ORG_RANK: Record<OrgRole, number> = { MEMBER: 1, ADMIN: 2, OWNER: 3 };
const AUDIT_LIMIT = 20;

/** 서버 메시지를 삼키지 않는다 — 예약어·중복·상한 문구가 그대로 보여야 한다. */
function msgOf(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function WorkspacePage(): JSX.Element {
  const auth = useAuth();
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const trialParam = searchParams.get('trial');
  const requestedTrial: TrialPlan | null =
    trialParam === 'pro' || trialParam === 'business' ? trialParam : null;
  const subscribeParam = searchParams.get('subscribe');
  const requestedSubscription: TrialPlan | null =
    subscribeParam === 'pro' || subscribeParam === 'business' ? subscribeParam : null;
  const paypalResult = searchParams.get('paypal');
  const paypalOrgId = searchParams.get('org');
  const paypalSubscriptionId = searchParams.get('subscription_id');
  const [phase, setPhase] = useState<Phase>('loading');
  const [fatal, setFatal] = useState<string | null>(null);
  /** 조작 실패 — 화면은 유지하고 상단 배너 한 곳에만 모은다(패널 onError 포함). */
  const [banner, setBanner] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<MyOrg[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createdNotice, setCreatedNotice] = useState<string | null>(null);
  const [trialNotice, setTrialNotice] = useState<string | null>(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const confirmAttempt = useRef<string | null>(null);
  const cancelHandled = useRef(false);

  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [auditState, setAuditState] = useState<AuditState>('hidden');

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.user) {
      setPhase('signin');
      return;
    }
    let alive = true;
    setPhase('loading');
    setFatal(null);
    void (async () => {
      try {
        // currentOrg 는 테넌트 서브도메인에서 "지금 이 워크스페이스"를 기본 선택하기 위한 보조 신호.
        // 실패·루트 도메인(null)은 구분할 필요가 없다 — 프리셀렉트만 포기하면 된다.
        const [orgsRes, tenantRes] = await Promise.all([
          api.myOrgs(),
          api.currentOrg().catch(() => ({ ok: true as const, tenant: null })),
        ]);
        if (!alive) return;
        const list = orgsRes.orgs;
        setOrgs(list);
        const tenantOrgId = tenantRes.tenant?.orgId ?? null;
        setSelectedId((prev) => {
          if (prev && list.some((o) => o.id === prev)) return prev;
          if (tenantOrgId && list.some((o) => o.id === tenantOrgId)) return tenantOrgId;
          return list[0]?.id ?? null;
        });
        setPhase('ready');
      } catch (err) {
        if (!alive) return;
        setFatal(msgOf(err, t({ ko: '워크스페이스 목록을 불러오지 못했습니다.', en: 'Could not load workspaces.' })));
        setPhase('error');
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.loading, auth.user, retryKey]);

  const selected = orgs.find((o) => o.id === selectedId) ?? null;
  const selectedRole = selected?.role ?? null;

  useEffect(() => {
    if (paypalResult !== 'cancelled' || cancelHandled.current) return;
    cancelHandled.current = true;
    setBillingNotice(
      t({
        ko: 'PayPal 구독 승인이 취소되었습니다. 결제나 구독은 생성되지 않았습니다.',
        en: 'PayPal subscription approval was cancelled. No payment or subscription was created.',
      }),
    );
    const next = new URLSearchParams(searchParams);
    for (const key of ['paypal', 'org', 'plan', 'subscription_id', 'ba_token', 'token']) {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalResult, searchParams, setSearchParams]);

  useEffect(() => {
    if (phase !== 'ready' || paypalResult !== 'success' || !paypalOrgId || !paypalSubscriptionId) {
      return;
    }
    const key = `${paypalOrgId}:${paypalSubscriptionId}`;
    if (confirmAttempt.current === key) return;
    confirmAttempt.current = key;
    setBillingBusy(true);
    setBanner(null);
    setSelectedId(paypalOrgId);
    void (async () => {
      try {
        const confirmed = await api.confirmPayPalSubscription(paypalOrgId, paypalSubscriptionId);
        const refreshed = await api.myOrgs();
        setOrgs(refreshed.orgs);
        setBillingNotice(
          t({
            ko: `${confirmed.subscription.plan.toUpperCase()} PayPal 월 구독이 활성화되었습니다.`,
            en: `${confirmed.subscription.plan.toUpperCase()} PayPal monthly subscription is now active.`,
          }),
        );
        const next = new URLSearchParams(searchParams);
        for (const param of [
          'paypal',
          'org',
          'plan',
          'subscribe',
          'subscription_id',
          'ba_token',
          'token',
        ]) {
          next.delete(param);
        }
        setSearchParams(next, { replace: true });
      } catch (err) {
        setBanner(
          msgOf(err, t({ ko: 'PayPal 구독 상태를 확인하지 못했습니다.', en: 'Could not verify PayPal subscription status.' })),
        );
      } finally {
        setBillingBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, paypalResult, paypalOrgId, paypalSubscriptionId]);

  async function startTrial(): Promise<void> {
    if (!selected || !requestedTrial || trialBusy) return;
    setTrialBusy(true);
    setBanner(null);
    try {
      const response = await api.startOrgTrial(selected.id, requestedTrial);
      setOrgs((current) =>
        current.map((org) => (org.id === selected.id ? { ...org, ...response.org } : org)),
      );
      const ends = new Date(response.org.trialEndsAt ?? '').toLocaleDateString('ko-KR');
      setTrialNotice(
        t({
          ko: `${response.org.plan.toUpperCase()} 30일 무료 체험이 시작되었습니다. ${ends}까지 이용할 수 있으며 자동 결제되지 않습니다.`,
          en: `${response.org.plan.toUpperCase()} 30-day free trial has started. You can use it until ${ends}, with no automatic billing.`,
        }),
      );
      setSearchParams({}, { replace: true });
    } catch (err) {
      setBanner(msgOf(err, t({ ko: '무료 체험을 시작하지 못했습니다.', en: 'Could not start the free trial.' })));
    } finally {
      setTrialBusy(false);
    }
  }

  async function startPayPalSubscription(): Promise<void> {
    if (!selected || !requestedSubscription || billingBusy) return;
    setBillingBusy(true);
    setBanner(null);
    try {
      const response = await api.createPayPalSubscription(selected.id, requestedSubscription);
      navigateTo(response.approvalUrl);
    } catch (err) {
      setBanner(msgOf(err, t({ ko: 'PayPal 구독을 시작하지 못했습니다.', en: 'Could not start the PayPal subscription.' })));
      setBillingBusy(false);
    }
  }

  // 감사로그 — ADMIN 이상만 호출(서버가 403 을 주는 경로를 뻔히 두드리지 않는다).
  useEffect(() => {
    if (
      phase !== 'ready' ||
      !selectedId ||
      !selectedRole ||
      ORG_RANK[selectedRole] < ORG_RANK.ADMIN
    ) {
      setAudit([]);
      setAuditState('hidden');
      return;
    }
    let alive = true;
    setAuditState('loading');
    api
      .orgAudit(selectedId, AUDIT_LIMIT)
      .then((r) => {
        if (!alive) return;
        setAudit(r.entries);
        setAuditState('ready');
      })
      .catch((err) => {
        if (!alive) return;
        // 역할이 낡아 403 이면 섹션을 조용히 접는다 — 에러가 아니라 권한 경계다.
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          setAuditState('hidden');
          return;
        }
        setAuditState('error');
      });
    return () => {
      alive = false;
    };
  }, [phase, selectedId, selectedRole]);

  const handleCreated = useCallback(
    async (org: { id: string; subdomain: string; name: string }) => {
      // ★정직 고지 — 서브도메인 주소는 운영자의 DNS 준비가 끝나야 열린다(서버가 그렇게 동작한다).
      setCreatedNotice(
        t({
          ko:
            `워크스페이스 '${org.name}'이(가) 생성되었습니다. 주소 https://${org.subdomain}.cloudbtl.com 은(는) ` +
            `DNS 준비(운영자 작업)가 끝난 뒤에 열립니다 — 생성 직후에는 접속되지 않을 수 있습니다. ` +
            `준비 상태 문의: hi@cloudbtl.com`,
          en:
            `Workspace '${org.name}' has been created. The address https://${org.subdomain}.cloudbtl.com ` +
            `will open once DNS setup (operator task) is complete — it may not be reachable right after creation. ` +
            `For setup status, contact hi@cloudbtl.com`,
        }),
      );
      setShowCreate(false);
      try {
        const refreshed = await api.myOrgs();
        setOrgs(refreshed.orgs);
        setSelectedId(org.id);
      } catch (err) {
        setBanner(
          msgOf(err, t({ ko: '생성은 됐지만 목록 재조회에 실패했습니다. 새로고침해 주세요.', en: 'Created, but reloading the list failed. Please refresh.' })),
        );
      }
    },
    [t],
  );
  const handlePanelError = useCallback((message: string) => setBanner(message), []);

  return (
    <Shell>
      <section style={S.page}>
        <h1 style={S.h1}>{t({ ko: '워크스페이스', en: 'Workspace' })}</h1>

        {phase === 'loading' ? (
          <p style={S.dim} role="status">
            {t({ ko: '불러오는 중…', en: 'Loading…' })}
          </p>
        ) : null}

        {phase === 'signin' ? (
          <p style={S.dim} data-testid="workspace-signin">
            {t({ ko: '워크스페이스를 관리하려면 ', en: 'Sign in is required to manage your workspace: ' })}
            <Link
              to={
                searchParams.toString()
                  ? `/login?returnTo=${encodeURIComponent(`/workspace?${searchParams.toString()}`)}`
                  : '/login'
              }
            >
              {t({ ko: '로그인', en: 'Sign in' })}
            </Link>
            {t({ ko: '이 필요합니다.', en: '.' })}
          </p>
        ) : null}

        {phase === 'error' ? (
          <div>
            <p style={S.error} role="alert">
              {fatal}
            </p>
            <button type="button" style={S.toolBtn} onClick={() => setRetryKey((k) => k + 1)}>
              {t({ ko: '다시 시도', en: 'Try again' })}
            </button>
          </div>
        ) : null}

        {banner ? (
          <p style={S.error} role="alert" data-testid="workspace-banner">
            {banner}
          </p>
        ) : null}

        {createdNotice ? (
          <p style={S.notice} role="status" data-testid="workspace-created-notice">
            {createdNotice}
          </p>
        ) : null}

        {trialNotice ? (
          <p style={S.notice} role="status" data-testid="trial-notice">
            {trialNotice}
          </p>
        ) : null}

        {billingNotice ? (
          <p style={S.notice} role="status" data-testid="billing-notice">
            {billingNotice}
          </p>
        ) : null}

        {phase === 'ready' && !selected ? (
          <>
            <p style={S.dim}>
              {t({
                ko: '아직 워크스페이스가 없습니다. 팀 자료실을 쓰려면 먼저 워크스페이스를 만드세요.',
                en: "You don't have a workspace yet. Create one first to use your team's library.",
              })}
            </p>
            <CreateOrgForm
              emailVerified={auth.user?.emailVerified ?? false}
              onCreated={handleCreated}
            />
          </>
        ) : null}

        {phase === 'ready' && selected ? (
          <>
            {orgs.length > 1 ? (
              <label style={S.selectorRow}>
                {t({ ko: '워크스페이스 선택', en: 'Select workspace' })}
                <select
                  style={S.roleSelect}
                  aria-label={t({ ko: '워크스페이스 선택', en: 'Select workspace' })}
                  value={selected.id}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} ({o.subdomain})
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <div style={S.card} data-testid="org-card">
              <div style={S.cardHead}>
                <h2 style={S.h2}>{selected.name}</h2>
                <span style={S.roleBadge}>
                  {t({ ko: '내 역할: ', en: 'My role: ' })}
                  {t(ROLE_LABEL[selected.role])}
                </span>
              </div>
              <p style={S.addr} data-testid="org-address">
                <a href={`https://${selected.subdomain}.cloudbtl.com/documents`} style={{ color: 'inherit' }}>
                  https://{selected.subdomain}.cloudbtl.com
                </a>{' '}
                <a
                  href={`https://${selected.subdomain}.cloudbtl.com/documents`}
                  className="link"
                  data-testid="org-open"
                >
                  {t({ ko: '열기 →', en: 'Open →' })}
                </a>
              </p>
              <p style={S.dimSmall}>
                {t({
                  ko: '주소가 열리지 않으면 아직 DNS 준비 전입니다 — 운영자 작업이 필요합니다.',
                  en: "If the address doesn't open, DNS isn't ready yet — this requires operator action.",
                })}
              </p>
              <p style={S.planRow} data-testid="org-plan">
                {t({ ko: '플랜: ', en: 'Plan: ' })}
                <strong>{selected.plan.toUpperCase()}</strong>
                {selected.trialActive && selected.trialEndsAt ? (
                  <span style={S.trialBadge} data-testid="active-trial">
                    {t({ ko: '30일 무료 체험 · ', en: '30-day free trial · until ' })}
                    {new Date(selected.trialEndsAt).toLocaleDateString('ko-KR')}
                    {t({ ko: '까지', en: '' })}
                  </span>
                ) : null}
                <span style={S.dimSmall} data-testid="upgrade-contact">
                  {' '}
                  {t({ ko: '· 유료 구독은 PayPal에서 결제·관리됩니다. ', en: '· Paid subscriptions are billed and managed via PayPal. ' })}
                  <Link to="/plans">{t({ ko: '플랜 보기', en: 'See plans' })}</Link>
                </span>
              </p>
            </div>

            {requestedTrial && !selected.trialActive ? (
              <div style={S.trialCard} data-testid="trial-offer">
                <h3 style={S.h3}>
                  {requestedTrial.toUpperCase()} {t({ ko: '30일 무료 체험', en: '30-day free trial' })}
                </h3>
                <p style={S.dimSmall}>
                  {t({
                    ko: '카드 등록 없이 즉시 시작합니다. 30일 뒤 자동 결제 없이 Free 플랜으로 돌아갑니다.',
                    en: 'Starts immediately with no card required. After 30 days you return to the Free plan with no automatic billing.',
                  })}
                </p>
                {selected.role !== 'OWNER' ? (
                  <p style={S.warn}>
                    {t({ ko: '무료 체험은 워크스페이스 소유자만 시작할 수 있습니다.', en: 'Only the workspace owner can start a free trial.' })}
                  </p>
                ) : !auth.user?.emailVerified ? (
                  <p style={S.warn}>
                    {t({ ko: '무료 체험을 시작하려면 먼저 이메일을 인증해 주세요.', en: 'Please verify your email before starting a free trial.' })}
                  </p>
                ) : (selected.basePlan ?? selected.plan) !== 'free' ? (
                  <p style={S.dim}>
                    {t({ ko: '이미 유료 플랜을 사용 중인 워크스페이스입니다.', en: 'This workspace is already on a paid plan.' })}
                  </p>
                ) : selected.trialStartedAt ? (
                  <p style={S.dim}>
                    {t({ ko: '이 워크스페이스의 무료 체험은 이미 사용되었습니다.', en: "This workspace's free trial has already been used." })}
                  </p>
                ) : (
                  <button
                    type="button"
                    style={S.primaryBtn}
                    disabled={trialBusy}
                    onClick={() => void startTrial()}
                  >
                    {trialBusy
                      ? t({ ko: '체험 시작 중…', en: 'Starting trial…' })
                      : t({ ko: '무료 체험 시작하기', en: 'Start free trial' })}
                  </button>
                )}
              </div>
            ) : null}

            {requestedSubscription ? (
              <div style={S.billingCard} data-testid="paypal-offer">
                <h3 style={S.h3}>
                  {requestedSubscription.toUpperCase()} {t({ ko: 'PayPal 월 구독', en: 'PayPal monthly subscription' })}
                </h3>
                <p style={S.billingPrice}>
                  {requestedSubscription === 'pro' ? '$10' : '$30'} {t({ ko: 'USD / 월', en: 'USD / month' })}
                </p>
                <p style={S.dimSmall}>
                  {t({
                    ko: '계속하면 PayPal로 이동합니다. PayPal 승인 화면에 표시된 금액과 청구 주기를 확인한 뒤 동의해야 구독이 시작됩니다.',
                    en: "Continuing takes you to PayPal. Review the amount and billing cycle shown on PayPal's approval screen and agree to start the subscription.",
                  })}
                </p>
                {selected.role !== 'OWNER' ? (
                  <p style={S.warn}>
                    {t({ ko: '유료 구독은 워크스페이스 소유자만 시작할 수 있습니다.', en: 'Only the workspace owner can start a paid subscription.' })}
                  </p>
                ) : !auth.user?.emailVerified ? (
                  <p style={S.warn}>
                    {t({ ko: '구독을 시작하려면 먼저 이메일을 인증해 주세요.', en: 'Please verify your email before starting a subscription.' })}
                  </p>
                ) : (selected.basePlan ?? selected.plan) === 'pro' ||
                  (selected.basePlan ?? selected.plan) === 'business' ? (
                  <p style={S.dim}>
                    {t({ ko: '이미 유료 플랜을 사용 중인 워크스페이스입니다.', en: 'This workspace is already on a paid plan.' })}
                  </p>
                ) : (
                  <button
                    type="button"
                    style={S.paypalBtn}
                    disabled={billingBusy}
                    onClick={() => void startPayPalSubscription()}
                  >
                    {billingBusy
                      ? t({ ko: 'PayPal 연결 중…', en: 'Connecting to PayPal…' })
                      : t({ ko: 'PayPal에서 구독 승인하기', en: 'Approve subscription on PayPal' })}
                  </button>
                )}
              </div>
            ) : null}

            <OrgMembers
              orgId={selected.id}
              myRole={selected.role}
              myUserId={auth.user?.id ?? null}
              orgName={selected.name}
              subdomain={selected.subdomain}
              onError={handlePanelError}
            />

            <EnterpriseSsoPanel
              orgId={selected.id}
              subdomain={selected.subdomain}
              myRole={selected.role}
              onError={handlePanelError}
            />

            {auditState !== 'hidden' ? (
              <div style={S.card} data-testid="audit-section">
                <h3 style={S.h3}>
                  {t({ ko: `최근 관리 활동 (최근 ${AUDIT_LIMIT}건)`, en: `Recent admin activity (last ${AUDIT_LIMIT})` })}
                </h3>
                {auditState === 'loading' ? (
                  <p style={S.dim} role="status">
                    {t({ ko: '불러오는 중…', en: 'Loading…' })}
                  </p>
                ) : null}
                {auditState === 'error' ? (
                  <p style={S.error}>{t({ ko: '감사 로그를 불러오지 못했습니다.', en: 'Could not load the audit log.' })}</p>
                ) : null}
                {auditState === 'ready' && audit.length === 0 ? (
                  <p style={S.dim}>{t({ ko: '기록이 없습니다.', en: 'No records.' })}</p>
                ) : null}
                {auditState === 'ready' && audit.length > 0 ? (
                  <ul style={S.auditList}>
                    {audit.map((e) => (
                      <li key={e.id} style={S.auditRow} data-testid="audit-row">
                        <span style={S.auditTime}>
                          {new Date(e.createdAt).toLocaleString('ko-KR')}
                        </span>
                        <span style={S.auditActor}>{e.actorEmail}</span>
                        <span style={S.auditAction}>
                          {e.action} · {e.targetType}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {showCreate ? (
              <CreateOrgForm
                emailVerified={auth.user?.emailVerified ?? false}
                onCreated={handleCreated}
              />
            ) : (
              <button
                type="button"
                style={S.toolBtn}
                onClick={() => setShowCreate(true)}
                data-testid="show-create"
              >
                {t({ ko: '새 워크스페이스 만들기', en: 'Create a new workspace' })}
              </button>
            )}
          </>
        ) : null}
      </section>
    </Shell>
  );
}

function CreateOrgForm({
  emailVerified,
  onCreated,
}: {
  emailVerified: boolean;
  onCreated: (org: { id: string; subdomain: string; name: string }) => Promise<void>;
}): JSX.Element {
  const t = useT();
  const [sub, setSub] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const s = sub.trim().toLowerCase();
    if (!s || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.createOrg(s, name.trim() || undefined);
      await onCreated(res.org);
    } catch (e) {
      // 예약어·중복·상한·미검증 — 전부 서버 문구를 그대로 (클라 사전판정은 낡을 수 있다).
      setErr(msgOf(e, t({ ko: '워크스페이스 생성에 실패했습니다.', en: 'Failed to create workspace.' })));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form style={S.createForm} onSubmit={submit} data-testid="workspace-create">
      <h3 style={S.h3}>{t({ ko: '워크스페이스 만들기', en: 'Create a workspace' })}</h3>
      {!emailVerified ? (
        <p style={S.warn} data-testid="verify-warning">
          {t({
            ko: '이메일 인증이 필요한 작업입니다 — 인증 전에는 서버가 생성을 거부합니다.',
            en: 'This action requires email verification — the server rejects creation until you verify.',
          })}
        </p>
      ) : null}
      <label style={S.fieldLabel}>
        {t({ ko: '서브도메인', en: 'Subdomain' })}
        <input
          style={S.input}
          aria-label={t({ ko: '서브도메인', en: 'Subdomain' })}
          value={sub}
          onChange={(e) => setSub(e.target.value)}
          placeholder="acme"
          maxLength={40}
          disabled={busy}
        />
      </label>
      <p style={S.dimSmall}>
        {t({
          ko: '소문자·숫자·하이픈, 3~40자, 하이픈으로 시작/끝 불가. 예약어·중복 여부는 생성 시 서버가 판정합니다.',
          en: 'Lowercase letters, numbers, and hyphens, 3–40 characters, cannot start/end with a hyphen. Reserved words and duplicates are judged by the server at creation.',
        })}
      </p>
      <label style={S.fieldLabel}>
        {t({ ko: '이름 (선택)', en: 'Name (optional)' })}
        <input
          style={S.input}
          aria-label={t({ ko: '워크스페이스 이름', en: 'Workspace name' })}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t({ ko: '에이크미 주식회사', en: 'Acme Inc.' })}
          maxLength={120}
          disabled={busy}
        />
      </label>
      {/* ★정직 고지 — 생성 ≠ 주소 개통. DNS 는 운영자 작업이 따로 필요하다. */}
      <p style={S.warn} data-testid="dns-honesty">
        {t({
          ko: '생성 즉시 서브도메인 주소가 열리는 것은 아닙니다 — DNS 준비(운영자 작업)가 끝난 뒤 접속할 수 있습니다.',
          en: "The subdomain address doesn't open immediately upon creation — it becomes reachable after DNS setup (operator task) is complete.",
        })}
      </p>
      {err ? (
        <p style={S.error} role="alert" data-testid="create-error">
          {err}
        </p>
      ) : null}
      <button type="submit" style={S.primaryBtn} disabled={busy || !sub.trim()}>
        {busy ? t({ ko: '생성 중…', en: 'Creating…' }) : t({ ko: '워크스페이스 생성', en: 'Create workspace' })}
      </button>
    </form>
  );
}

const S: Record<string, CSSProperties> = {
  page: { maxWidth: 760, margin: '0 auto', padding: '24px 16px' },
  h1: { fontSize: 22, fontWeight: 800, margin: '0 0 14px', color: 'var(--color-fg)' },
  h2: { fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--color-fg)' },
  h3: { fontSize: 13.5, fontWeight: 700, margin: '0 0 10px', color: 'var(--color-fg)' },
  dim: { fontSize: 13, color: 'var(--color-subtle)', margin: '6px 0' },
  dimSmall: { fontSize: 12, color: 'var(--color-subtle)', margin: '4px 0' },
  error: {
    fontSize: 12.5,
    color: 'var(--color-danger, #d92d20)',
    margin: '6px 0',
    lineHeight: 1.5,
  },
  warn: {
    fontSize: 12.5,
    color: 'var(--color-warning, #b54708)',
    margin: '6px 0',
    lineHeight: 1.5,
  },
  notice: {
    fontSize: 12.5,
    color: 'var(--color-fg)',
    background: 'var(--surface-hover)',
    border: '1px solid var(--color-border)',
    borderRadius: 7,
    padding: '8px 10px',
    margin: '0 0 12px',
    lineHeight: 1.6,
  },
  card: {
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
    background: 'var(--surface-sunken)',
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  roleBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-subtle)',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 5,
    padding: '2px 7px',
  },
  addr: { fontSize: 13, margin: '8px 0 0', color: 'var(--color-fg)', fontFamily: 'monospace' },
  planRow: { fontSize: 13, margin: '8px 0 0', color: 'var(--color-fg)' },
  trialBadge: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '2px 7px',
    borderRadius: 999,
    background: 'color-mix(in srgb, var(--color-accent) 14%, transparent)',
    color: 'var(--color-accent)',
    fontSize: 11.5,
    fontWeight: 700,
  },
  trialCard: {
    border: '1px solid var(--color-accent)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
    background: 'color-mix(in srgb, var(--color-accent) 7%, var(--color-card))',
  },
  billingCard: {
    border: '1px solid #0070ba',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
    background: 'color-mix(in srgb, #0070ba 7%, var(--color-card))',
  },
  billingPrice: {
    margin: '4px 0 8px',
    color: 'var(--color-fg)',
    fontSize: 18,
    fontWeight: 800,
  },
  selectorRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12 },
  roleSelect: {
    fontSize: 13,
    padding: '7px 8px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
  },
  createForm: {
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
    background: 'var(--surface-sunken)',
  },
  fieldLabel: { display: 'block', fontSize: 12.5, color: 'var(--color-fg)', margin: '8px 0 2px' },
  input: {
    display: 'block',
    width: '100%',
    fontSize: 13,
    padding: '7px 10px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
    marginTop: 4,
  },
  primaryBtn: {
    fontSize: 13,
    padding: '8px 14px',
    borderRadius: 7,
    border: 'none',
    background: 'var(--color-accent)',
    color: '#fff',
    cursor: 'pointer',
    marginTop: 6,
  },
  paypalBtn: {
    fontSize: 13,
    fontWeight: 700,
    padding: '9px 14px',
    borderRadius: 7,
    border: 'none',
    background: '#0070ba',
    color: '#fff',
    cursor: 'pointer',
    marginTop: 6,
  },
  toolBtn: {
    fontSize: 12.5,
    padding: '6px 11px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
    cursor: 'pointer',
  },
  auditList: { listStyle: 'none', margin: 0, padding: 0 },
  auditRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'baseline',
    padding: '6px 2px',
    borderTop: '1px solid var(--color-border)',
    flexWrap: 'wrap',
  },
  auditTime: { fontSize: 11.5, color: 'var(--color-subtle)', whiteSpace: 'nowrap' },
  auditActor: { fontSize: 12.5, color: 'var(--color-fg)' },
  auditAction: { fontSize: 12, color: 'var(--color-subtle)' },
};
